import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, Check, Link2, Pencil, Plus, Search, UsersRound, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { DataPanel, StatusPill } from '../../components/DataStates';
import { InlineSpinner } from '../../components/LoadingIndicators';
import { apiRequest, getApiErrorMessage } from '../../lib/api';
import { useApiCollection } from '../../lib/useApiCollection';
import type { Organization, OrganizationStatus, OrganizationType } from '../../types/domain';

interface OrganizationDraft {
  name: string;
  legal_name: string;
  short_name: string;
  slug: string;
  type: OrganizationType;
  status: OrganizationStatus;
  logo: File | null;
  primary_contact_name: string;
  primary_contact_email: string;
  primary_contact_phone: string;
  website: string;
  address: string;
  city: string;
  country: string;
}

const emptyDraft: OrganizationDraft = {
  name: '',
  legal_name: '',
  short_name: '',
  slug: '',
  type: 'agency',
  status: 'draft',
  logo: null,
  primary_contact_name: '',
  primary_contact_email: '',
  primary_contact_phone: '',
  website: '',
  address: '',
  city: '',
  country: 'Pakistan',
};

const emptyOnboarding: Organization['onboarding'] = {
  completed: 0,
  total: 8,
  percentage: 0,
  is_complete: false,
  items: [],
};

function normalizeOrganization(organization: Organization): Organization {
  return {
    ...organization,
    legal_name: organization.legal_name ?? '',
    short_name: organization.short_name ?? '',
    status: organization.status ?? (organization.is_active ? 'active' : 'suspended'),
    logo: organization.logo ?? null,
    primary_contact_name: organization.primary_contact_name ?? '',
    primary_contact_email: organization.primary_contact_email ?? '',
    primary_contact_phone: organization.primary_contact_phone ?? '',
    website: organization.website ?? '',
    address: organization.address ?? '',
    city: organization.city ?? '',
    country: organization.country ?? '',
    onboarding: organization.onboarding ?? emptyOnboarding,
  };
}

function slugify(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function fileNameFromUrl(value: string | null) {
  if (!value) return null;
  const [path = ''] = value.split(/[?#]/);
  const fileName = path.split('/').filter(Boolean).pop();
  if (!fileName) return null;
  try {
    return decodeURIComponent(fileName);
  } catch {
    return fileName;
  }
}

export function OrganizationsPage() {
  const { accessToken, capabilities, isPlatformAdmin, selectedWorkspace } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Organization | 'new' | null>(null);
  const [draft, setDraft] = useState<OrganizationDraft>(emptyDraft);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timeout);
  }, [search]);
  const organizationParams = new URLSearchParams({ page_size: '100', ordering: 'name' });
  if (debouncedSearch) organizationParams.set('search', debouncedSearch);
  const organizations = useApiCollection<Organization>(
    'organizations-onboarding-v1',
    `/organizations/?${organizationParams.toString()}`,
    accessToken,
  );
  const rows = organizations.data?.results.map(normalizeOrganization) ?? [];

  const mutation = useMutation({
    mutationFn: async () => {
      const path = editing === 'new' ? '/organizations/' : `/organizations/${editing?.id}/`;
      const body = new FormData();
      body.set('name', draft.name);
      body.set('legal_name', draft.legal_name);
      body.set('short_name', draft.short_name);
      body.set('primary_contact_name', draft.primary_contact_name);
      body.set('primary_contact_email', draft.primary_contact_email);
      body.set('primary_contact_phone', draft.primary_contact_phone);
      body.set('website', draft.website);
      body.set('address', draft.address);
      body.set('city', draft.city);
      body.set('country', draft.country);
      if (draft.logo) body.set('logo', draft.logo);
      if (isPlatformAdmin) {
        body.set('slug', draft.slug);
        body.set('status', draft.status);
        if (editing === 'new') body.set('type', draft.type);
      }
      return apiRequest<Organization>(path, {
        ...(accessToken ? { accessToken } : {}),
        method: editing === 'new' ? 'POST' : 'PATCH',
        body,
      });
    },
    onSuccess: async () => {
      setEditing(null);
      setDraft(emptyDraft);
      await queryClient.invalidateQueries({
        queryKey: ['collection', 'organizations-onboarding-v1'],
      });
    },
  });

  const canManage = (organization: Organization) => isPlatformAdmin || (
    capabilities.includes('organizations:manage')
    && selectedWorkspace?.organizationId === organization.id
  );
  const currentLogoName = editing !== null && editing !== 'new'
    ? fileNameFromUrl(editing.logo)
    : null;
  const displayedLogoName = draft.logo?.name ?? currentLogoName ?? 'No file chosen';

  function beginCreate() {
    mutation.reset();
    setDraft(emptyDraft);
    setEditing('new');
  }

  function beginEdit(organization: Organization) {
    mutation.reset();
    setDraft({
      name: organization.name,
      legal_name: organization.legal_name,
      short_name: organization.short_name,
      slug: organization.slug,
      type: organization.type,
      status: organization.status,
      logo: null,
      primary_contact_name: organization.primary_contact_name,
      primary_contact_email: organization.primary_contact_email,
      primary_contact_phone: organization.primary_contact_phone,
      website: organization.website,
      address: organization.address,
      city: organization.city,
      country: organization.country,
    });
    setEditing(organization);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    mutation.mutate();
  }

  function closeForm() {
    if (mutation.isPending) return;
    setEditing(null);
    setDraft(emptyDraft);
    mutation.reset();
  }

  return (
    <>
      <header className="page-heading">
        <div>
          <p className="eyebrow">{selectedWorkspace?.name}</p>
          <h1>Organisations</h1>
          <p>Platform-controlled onboarding for agency and client workspaces.</p>
        </div>
        {!isPlatformAdmin ? (
          <span className="record-count"><Building2 size={16} /> {organizations.data?.count ?? 0}</span>
        ) : null}
      </header>

      {editing ? (
        <div
          className="confirmation-backdrop"
          onMouseDown={(event) => { if (event.target === event.currentTarget) closeForm(); }}
          role="presentation"
        >
          <section
            aria-labelledby="organization-form-dialog-title"
            aria-modal="true"
            className="confirmation-dialog organization-form-dialog"
            role="dialog"
          >
            <button
              aria-label="Close organisation form"
              className="icon-button confirmation-close"
              disabled={mutation.isPending}
              onClick={closeForm}
              type="button"
            >
              <X size={19} />
            </button>
            <div className="card-heading organization-form-dialog-heading">
            <div>
              <h2 id="organization-form-dialog-title">{editing === 'new' ? 'Add organisation' : `Edit ${editing.name}`}</h2>
              <p>{editing === 'new' ? 'Create a draft workspace, then invite its administrator.' : 'Complete the organisation profile and onboarding requirements.'}</p>
            </div>
          </div>

          {editing !== 'new' ? (
            <div className="onboarding-summary">
              <div>
                <strong>{editing.onboarding.percentage}% onboarding complete</strong>
                <span>{editing.onboarding.completed} of {editing.onboarding.total} requirements</span>
              </div>
              <div className="progress-track" aria-label={`${editing.onboarding.percentage}% complete`}>
                <span style={{ width: `${editing.onboarding.percentage}%` }} />
              </div>
              <div className="checklist-grid">
                {editing.onboarding.items.map((item) => (
                  <span className={item.complete ? 'complete' : ''} key={item.key}>
                    <Check size={14} /> {item.label}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          <form className="management-form" onSubmit={submit}>
            <div className="form-grid">
              <label>
                Display name
                <input
                  required
                  value={draft.name}
                  onChange={(event) => setDraft((current) => ({
                    ...current,
                    name: event.target.value,
                    slug: editing === 'new' ? slugify(event.target.value) : current.slug,
                  }))}
                />
              </label>
              <label>
                Legal name
                <input value={draft.legal_name} onChange={(event) => setDraft({ ...draft, legal_name: event.target.value })} />
              </label>
              {draft.type === 'client' ? (
                <label>
                  Short name for charts
                  <input
                    maxLength={32}
                    onChange={(event) => setDraft({ ...draft, short_name: event.target.value })}
                    placeholder="For example, POML"
                    value={draft.short_name}
                  />
                  <small>Optional. Used at the centre of the sales-by-brand chart.</small>
                </label>
              ) : null}
              {isPlatformAdmin ? (
                <>
                  <label>
                    Slug
                    <input required pattern="[a-z0-9]+(?:-[a-z0-9]+)*" value={draft.slug} onChange={(event) => setDraft({ ...draft, slug: slugify(event.target.value) })} />
                  </label>
                  <label>
                    Type
                    <select disabled={editing !== 'new'} value={draft.type} onChange={(event) => setDraft({ ...draft, type: event.target.value as OrganizationType })}>
                      <option value="agency">Agency</option>
                      <option value="client">Client</option>
                      <option value="platform">Platform</option>
                    </select>
                  </label>
                  <label>
                    Lifecycle status
                    <select value={draft.status} onChange={(event) => setDraft({ ...draft, status: event.target.value as OrganizationStatus })}>
                      <option value="draft">Draft</option>
                      <option value="pending_review">Pending review</option>
                      <option value="active">Active</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </label>
                </>
              ) : null}
              <label>
                Primary contact name
                <input value={draft.primary_contact_name} onChange={(event) => setDraft({ ...draft, primary_contact_name: event.target.value })} />
              </label>
              <label>
                Primary contact email
                <input type="email" value={draft.primary_contact_email} onChange={(event) => setDraft({ ...draft, primary_contact_email: event.target.value })} />
              </label>
              <label>
                Primary contact phone
                <input value={draft.primary_contact_phone} onChange={(event) => setDraft({ ...draft, primary_contact_phone: event.target.value })} />
              </label>
              <label>
                Website
                <input placeholder="https://" type="url" value={draft.website} onChange={(event) => setDraft({ ...draft, website: event.target.value })} />
              </label>
              <label>
                City
                <input value={draft.city} onChange={(event) => setDraft({ ...draft, city: event.target.value })} />
              </label>
              <label>
                Country
                <input value={draft.country} onChange={(event) => setDraft({ ...draft, country: event.target.value })} />
              </label>
              <label className="wide-field">
                Address
                <textarea rows={3} value={draft.address} onChange={(event) => setDraft({ ...draft, address: event.target.value })} />
              </label>
              <div className="form-field">
                <span>Logo</span>
                <div className="file-picker-control">
                  <input
                    accept="image/*"
                    aria-label="Choose replacement logo"
                    className="file-picker-input sr-only"
                    id="organization-logo"
                    onChange={(event) => setDraft({ ...draft, logo: event.target.files?.[0] ?? null })}
                    type="file"
                  />
                  <label className="file-picker-button" htmlFor="organization-logo">Choose file</label>
                  <span className="file-picker-name" title={displayedLogoName}>{displayedLogoName}</span>
                </div>
                {currentLogoName && !draft.logo ? <small>Current logo; choose a file only to replace it.</small> : null}
              </div>
            </div>
            {mutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(mutation.error)}</p> : null}
            <div className="form-actions">
              <button className="button secondary" disabled={mutation.isPending} onClick={closeForm} type="button">Cancel</button>
              <button className="button primary" disabled={mutation.isPending} type="submit">{mutation.isPending ? <InlineSpinner /> : null}{mutation.isPending ? 'Saving…' : 'Save organisation'}</button>
            </div>
          </form>
          </section>
        </div>
      ) : null}

      <div className="organization-list-toolbar">
        <label className="search-field">
          <Search aria-hidden="true" size={17} />
          <input
            aria-label="Search organisations"
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search organisation name or slug"
            type="search"
            value={search}
          />
        </label>
        {isPlatformAdmin ? (
          <div className="organization-list-toolbar-actions">
            <Link className="button secondary" to="/organisations/relationships">
              <Link2 size={17} /> Agency-client links
            </Link>
            <button className="button primary" onClick={beginCreate} type="button">
              <Plus size={17} /> Add organisation
            </button>
          </div>
        ) : null}
      </div>

      <DataPanel loading={organizations.isLoading} error={organizations.isError} empty={rows.length === 0}>
        <section className="content-card data-card">
          <div className="data-table-wrap">
            <table className="data-table">
              <thead><tr><th>Name</th><th>Type</th><th>Lifecycle</th><th>Onboarding</th><th><span className="sr-only">Actions</span></th></tr></thead>
              <tbody>
                {rows.map((organization) => (
                  <tr key={organization.id}>
                    <td><strong>{organization.name}</strong><small>{organization.city || organization.slug}</small></td>
                    <td><StatusPill value={organization.type} /></td>
                    <td><StatusPill value={organization.status} /></td>
                    <td><strong>{organization.onboarding.percentage}%</strong><small>{organization.onboarding.completed} of {organization.onboarding.total} complete</small></td>
                    <td>
                      {canManage(organization) ? (
                        <div className="table-actions">
                          <button className="text-button" onClick={() => beginEdit(organization)} type="button"><Pencil size={14} /> Edit</button>
                          <Link className="text-button" to={`/organisations/${organization.id}/members`}><UsersRound size={14} /> Members</Link>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </DataPanel>
    </>
  );
}
